import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Card, useSnackbar } from '@sauda/ui/kit';
import { api, dealErrorText } from '../../lib/api.js';
import { whatsappLink } from '../../lib/share-text.js';
import type { DealDetailView } from '../../lib/types.js';

interface Side {
  link: string | null;
  firstOpenedAt: string | null;
}
interface Links {
  tenant: Side | null;
  owner: Side | null;
}

/** Share the deal status page with the tenant and the owner: one link each, from the broker's own WhatsApp. */
export function StatusShare({ deal, canEdit }: { deal: DealDetailView; canEdit: boolean }) {
  const { t } = useTranslation();
  const cache = useQueryClient();
  const snack = useSnackbar();
  const links = useQuery({
    queryKey: ['status-links', deal.id],
    queryFn: () => api<Links>(`/deals/${deal.id}/status-links`),
  });
  const make = useMutation({
    mutationFn: () => api<Links>(`/deals/${deal.id}/status-links`, { method: 'POST', body: {} }),
    onSuccess: (made) => cache.setQueryData(['status-links', deal.id], made),
    onError: (e) => snack({ text: dealErrorText(e, t) }),
  });
  const data = links.data;
  const sides: { key: 'tenant' | 'owner'; phone: string | null }[] = [
    { key: 'tenant', phone: deal.client.phone },
    { key: 'owner', phone: deal.owner.phone },
  ];
  const have = data !== undefined && (data.tenant?.link != null || data.owner?.link != null);
  return (
    <Card>
      <h2 className="k-t-title-sm">{t('deal.statusShareTitle')}</h2>
      <p className="k-t-body-sm k-muted">{t('deal.statusShareHelp')}</p>
      {have ? (
        sides.map(({ key, phone }) => {
          const side = data?.[key];
          if (side?.link == null) return null;
          const text = t('deal.statusShareMessage', {
            agency: deal.propertyTitle ?? '',
            link: side.link,
          });
          return (
            <div key={key} className="deal-client__row">
              <div className="row-main k-grow">
                <p className="k-t-title-sm">{t(`deal.statusFor_${key}`)}</p>
                <p className="k-t-body-sm k-muted">
                  {side.firstOpenedAt === null ? t('deal.statusNotOpened') : t('deal.statusOpened')}
                </p>
              </div>
              <Button
                variant="tonal"
                onClick={() => {
                  void navigator.clipboard?.writeText(side.link as string);
                  snack({ text: t('property.linkCopied') });
                }}
              >
                {t('property.copyLink')}
              </Button>
              <a
                className="k-btn k-btn--tonal k-btn--md k-layer k-t-label-lg"
                href={whatsappLink(text, phone)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('visits.whatsapp')}
              </a>
            </div>
          );
        })
      ) : canEdit ? (
        <Button variant="tonal" loading={make.isPending} onClick={() => make.mutate()}>
          {t('deal.statusMake')}
        </Button>
      ) : null}
    </Card>
  );
}
